import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { POSTS } from "@/data/blogData";
import { ArrowUpRight } from "lucide-react";

/**
 * Blog index — trustee-narrated case study collection. Ranks for
 * "azure gypsy guild case study", "counterclaim mortgage success story",
 * and long-tail state + county combinations.
 */
export default function Blog() {
  useDocumentTitle(
    "Trustee Case Files — Azure Gypsy Guild Blog | 60-Day Foreclosure Wins",
    "Trustee-narrated case studies from the Azure Gypsy Guild ledger. Read how counterclaim + trustee-pledge settled real foreclosures in 30–60 days across Ohio, Texas, Florida and more.",
  );

  const canonical = "https://azuregypsyguild.org/blog";
  const ldBlog = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Azure Gypsy Guild — Trustee Case Files",
    "description": "Trustee-narrated 60-day foreclosure counterclaim case studies.",
    "url": canonical,
    "blogPost": POSTS.map((p) => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "datePublished": p.date,
      "author": { "@type": "Organization", "name": "Azure Gypsy Guild" },
      "url": `https://azuregypsyguild.org/blog/${p.slug}`,
      "description": p.excerpt,
      "image": p.cover,
    })),
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="blog-index">
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBlog) }} />
      <link rel="canonical" href={canonical} />

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 md:mb-24">
          <div className="lg:col-span-8">
            <div className="overline mb-6">§ Trustee Case Files · MMXXVI</div>
            <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.94] tracking-tighter text-balance">
              The case-files.<br />
              <span className="font-editorial italic text-stone-500">Ledger by ledger.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 self-end">
            <p className="text-lg text-stone-600 font-light leading-relaxed">
              Trustee-narrated case studies from the Guild ledger. Anonymized details.
              Illustrative of the counterclaim + trustee-pledge process across states.
            </p>
          </div>
        </div>

        <div className="border-t border-l border-stone-200 bg-white grid md:grid-cols-2" data-testid="blog-post-grid">
          {POSTS.map((p, i) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="border-r border-b border-stone-200 p-8 md:p-10 group hover:bg-stone-50 transition-colors"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="aspect-[16/10] mb-6 overflow-hidden border border-stone-200">
                <img
                  src={p.cover}
                  alt={p.coverAlt}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale contrast-[1.05] group-hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div className="caption text-stone-400 mb-4">
                {String(i + 1).padStart(2, "0")} · {new Date(p.date).toLocaleDateString(undefined, { month: "long", year: "numeric" })} · {p.readMinutes} min read
              </div>
              <h2 className="font-serif font-light text-2xl md:text-3xl leading-tight text-stone-900 group-hover:text-stone-600 transition-colors text-balance">
                {p.title}
              </h2>
              <p className="mt-4 text-stone-600 text-[15px] font-light leading-relaxed">
                {p.excerpt}
              </p>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                {p.tags.slice(0, 3).map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 border border-stone-200 px-2.5 py-1">
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-8 flex items-center gap-2 text-stone-900 font-mono text-[11px] uppercase tracking-[0.22em] group-hover:underline underline-offset-4">
                Read the file <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
